import { ExtensionProps } from "easy-email-extensions";
import { AdvancedType } from "easy-email-core";

/**
 * Copilotly Email Builder Configuration
 *
 * Brand Colors (Copilotly - differentiated from Conferbot):
 * - Primary Purple: #8B5CF6 (violet-500)
 * - Secondary Purple: #A855F7 (purple-500)
 * - Accent Teal: #2DD4BF (robot eyes)
 * - Dark: #1E293B (backgrounds)
 * - Light: #F1F5F9 (subtle backgrounds)
 */

// Define default categories for the email builder
export const defaultCategories: ExtensionProps["categories"] = [
  // ============================================================
  // BRANDED ELEMENTS - Your brand's styled components
  // ============================================================
  {
    label: "Branded Elements",
    active: true,
    blocks: [
      // ========== BRANDED BUTTONS ==========
      {
        type: AdvancedType.BUTTON,
        title: "Primary Button",
        payload: {
          attributes: {
            "background-color": "#8B5CF6",
            color: "#ffffff",
            "border-radius": "8px",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "0px",
            "inner-padding": "14px 32px",
            href: "#",
          },
          data: { value: { content: "Get Started" } },
        },
      },
      {
        type: AdvancedType.BUTTON,
        title: "Secondary Button",
        payload: {
          attributes: {
            "background-color": "#2DD4BF",
            color: "#1E293B",
            "border-radius": "8px",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "0px",
            "inner-padding": "14px 32px",
            href: "#",
          },
          data: { value: { content: "Learn More" } },
        },
      },
      {
        type: AdvancedType.BUTTON,
        title: "Outline Button",
        payload: {
          attributes: {
            "background-color": "#ffffff",
            color: "#8B5CF6",
            "border-radius": "8px",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            border: "2px solid #8B5CF6",
            padding: "0px",
            "inner-padding": "12px 30px",
            href: "#",
          },
          data: { value: { content: "View Demo" } },
        },
      },
      {
        type: AdvancedType.BUTTON,
        title: "Pill CTA",
        payload: {
          attributes: {
            "background-color": "#14B8A6",
            color: "#ffffff",
            "border-radius": "25px",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "0px",
            "inner-padding": "16px 40px",
            href: "#",
          },
          data: { value: { content: "Start Free Trial" } },
        },
      },
      {
        type: AdvancedType.BUTTON,
        title: "Dark Button",
        payload: {
          attributes: {
            "background-color": "#1E293B",
            color: "#ffffff",
            "border-radius": "8px",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "0px",
            "inner-padding": "14px 32px",
            href: "#",
          },
          data: { value: { content: "Explore Now" } },
        },
      },
      // ========== BRANDED TEXT ==========
      {
        type: AdvancedType.TEXT,
        title: "Hero Headline",
        payload: {
          attributes: {
            align: "center",
            color: "#0F172A",
            "font-size": "32px",
            "font-weight": "700",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px 20px 10px 20px",
          },
          data: { value: { content: "Your Amazing Headline Here" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Section Title",
        payload: {
          attributes: {
            align: "center",
            color: "#8B5CF6",
            "font-size": "24px",
            "font-weight": "700",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
          },
          data: { value: { content: "Section Title" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Body Text",
        payload: {
          attributes: {
            align: "left",
            color: "#475569",
            "font-size": "16px",
            "line-height": "1.6",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "10px 20px",
          },
          data: { value: { content: "Add your content here. This is styled for readability with proper line height and spacing." } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Fine Print",
        payload: {
          attributes: {
            align: "center",
            color: "#94A3B8",
            "font-size": "12px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "10px 20px",
          },
          data: { value: { content: "Fine print or disclaimer text goes here." } },
        },
      },
      // ========== DIVIDERS & SPACERS ==========
      {
        type: AdvancedType.DIVIDER,
        title: "Brand Divider",
        payload: {
          attributes: {
            "border-color": "#8B5CF6",
            "border-width": "2px",
            "border-style": "solid",
            width: "80px",
            padding: "20px 0",
          },
        },
      },
      {
        type: AdvancedType.DIVIDER,
        title: "Subtle Line",
        payload: {
          attributes: {
            "border-color": "#E2E8F0",
            "border-width": "1px",
            "border-style": "solid",
            padding: "15px 0",
          },
        },
      },
      {
        type: AdvancedType.SPACER,
        title: "Small Space",
        payload: { attributes: { height: "20px" } },
      },
      {
        type: AdvancedType.SPACER,
        title: "Medium Space",
        payload: { attributes: { height: "40px" } },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Copilotly Logo",
        payload: {
          attributes: {
            src: "https://www.copilotly.com/img/logo/copilotly-logo.png",
            alt: "Copilotly",
            width: "150px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
    ],
  },

  // ============================================================
  // SOCIAL PROOF - Build trust and credibility
  // ============================================================
  {
    label: "Social Proof",
    active: true,
    blocks: [
      // Star Ratings
      {
        type: AdvancedType.TEXT,
        title: "5-Star Rating",
        payload: {
          attributes: {
            align: "center",
            color: "#F59E0B",
            "font-size": "28px",
            padding: "10px 0",
          },
          data: { value: { content: "★★★★★" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "4-Star Rating",
        payload: {
          attributes: {
            align: "center",
            color: "#F59E0B",
            "font-size": "28px",
            padding: "10px 0",
          },
          data: { value: { content: "★★★★☆" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Rating with Text",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            padding: "15px 20px",
            "font-family": "'Segoe UI', Arial, sans-serif",
          },
          data: { value: { content: '<span style="color: #F59E0B; font-size: 24px;">★★★★★</span><br><span style="color: #64748B;">4.9 out of 5 based on 2,847 reviews</span>' } },
        },
      },
      // Testimonial Quote
      {
        type: AdvancedType.TEXT,
        title: "Testimonial Quote",
        payload: {
          attributes: {
            align: "center",
            color: "#334155",
            "font-size": "18px",
            "font-style": "italic",
            "line-height": "1.6",
            "font-family": "Georgia, serif",
            padding: "20px 30px",
          },
          data: { value: { content: '"This product completely transformed how we work. I can\'t imagine going back to the old way of doing things."' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Testimonial Author",
        payload: {
          attributes: {
            align: "center",
            color: "#64748B",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "5px 20px 20px 20px",
          },
          data: { value: { content: "— <strong>Sarah Johnson</strong>, CEO at TechCorp" } },
        },
      },
      // Trust Badges
      {
        type: AdvancedType.TEXT,
        title: "Trust Badge - SSL",
        payload: {
          attributes: {
            align: "center",
            color: "#10B981",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
          },
          data: { value: { content: "🔒 <strong>256-bit SSL Encrypted</strong> • Your data is safe with us" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Money Back Guarantee",
        payload: {
          attributes: {
            align: "center",
            color: "#8B5CF6",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
          },
          data: { value: { content: "💰 <strong>30-Day Money Back Guarantee</strong> • No questions asked" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Award Badge",
        payload: {
          attributes: {
            align: "center",
            color: "#7C3AED",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
          },
          data: { value: { content: "🏆 <strong>Best in Class 2024</strong> • Voted #1 by industry experts" } },
        },
      },
      // Social Proof Numbers
      {
        type: AdvancedType.TEXT,
        title: "User Count",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            padding: "20px",
            "font-family": "'Segoe UI', Arial, sans-serif",
          },
          data: { value: { content: '<span style="font-size: 42px; font-weight: 700; color: #2DD4BF;">50,000+</span><br><span style="color: #64748B;">Happy Customers Worldwide</span>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Stats Row",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            padding: "20px",
            "font-family": "'Segoe UI', Arial, sans-serif",
          },
          data: { value: { content: '<span style="color: #8B5CF6; font-weight: 600;">10M+</span> Messages Sent &nbsp;•&nbsp; <span style="color: #8B5CF6; font-weight: 600;">50K+</span> Active Users &nbsp;•&nbsp; <span style="color: #8B5CF6; font-weight: 600;">99.9%</span> Uptime' } },
        },
      },
      // As Seen In / Featured In
      {
        type: AdvancedType.TEXT,
        title: "As Seen In",
        payload: {
          attributes: {
            align: "center",
            color: "#94A3B8",
            "font-size": "12px",
            "text-transform": "uppercase",
            "letter-spacing": "2px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px 20px 10px 20px",
          },
          data: { value: { content: "As Featured In" } },
        },
      },
    ],
  },

  // ============================================================
  // E-COMMERCE - Product & Sales blocks
  // ============================================================
  {
    label: "E-commerce",
    active: true,
    blocks: [
      // Product Card Elements
      {
        type: AdvancedType.TEXT,
        title: "Product Name",
        payload: {
          attributes: {
            align: "center",
            color: "#0F172A",
            "font-size": "20px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 10px 5px 10px",
          },
          data: { value: { content: "Premium Wireless Headphones" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Product Price",
        payload: {
          attributes: {
            align: "center",
            color: "#8B5CF6",
            "font-size": "28px",
            "font-weight": "700",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "5px 10px",
          },
          data: { value: { content: "$99.99" } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Sale Price",
        payload: {
          attributes: {
            align: "center",
            "font-size": "24px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "10px",
          },
          data: { value: { content: '<span style="text-decoration: line-through; color: #94A3B8;">$149.99</span>&nbsp;&nbsp;<span style="color: #EF4444; font-weight: 700;">$99.99</span>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Discount Badge",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            padding: "10px",
            "font-family": "'Segoe UI', Arial, sans-serif",
          },
          data: { value: { content: '<span style="background-color: #EF4444; color: white; padding: 4px 12px; border-radius: 4px; font-weight: 600;">SAVE 33%</span>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Limited Stock",
        payload: {
          attributes: {
            align: "center",
            color: "#EF4444",
            "font-size": "14px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "10px",
          },
          data: { value: { content: "🔥 Only 5 left in stock!" } },
        },
      },
      // Coupon/Discount Code
      {
        type: AdvancedType.TEXT,
        title: "Coupon Code Box",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            padding: "20px",
            "font-family": "'Segoe UI', Arial, sans-serif",
          },
          data: { value: { content: '<div style="background: #F1F5F9; border: 2px dashed #8B5CF6; border-radius: 8px; padding: 20px; display: inline-block;"><span style="color: #64748B;">Use code:</span><br><span style="font-size: 24px; font-weight: 700; color: #8B5CF6; letter-spacing: 2px;">SAVE20</span><br><span style="color: #64748B; font-size: 14px;">for 20% off your first order</span></div>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Free Shipping",
        payload: {
          attributes: {
            align: "center",
            color: "#10B981",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px",
          },
          data: { value: { content: "🚚 FREE SHIPPING on orders over $50" } },
        },
      },
      // Order Summary
      {
        type: AdvancedType.TEXT,
        title: "Order Summary",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
            "background-color": "#F8FAFC",
          },
          data: { value: { content: '<div style="border-bottom: 1px solid #E2E8F0; padding-bottom: 10px; margin-bottom: 10px;"><strong style="color: #0F172A;">Order Summary</strong></div><table style="width: 100%; color: #475569;"><tr><td>Subtotal</td><td style="text-align: right;">$99.99</td></tr><tr><td>Shipping</td><td style="text-align: right;">$9.99</td></tr><tr><td>Tax</td><td style="text-align: right;">$8.50</td></tr></table><div style="border-top: 2px solid #8B5CF6; margin-top: 10px; padding-top: 10px;"><strong style="color: #0F172A; font-size: 18px;">Total: $118.48</strong></div>' } },
        },
      },
      // Cart Reminder
      {
        type: AdvancedType.TEXT,
        title: "Cart Reminder",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
            "background-color": "#FEF3C7",
          },
          data: { value: { content: '<span style="font-size: 24px;">🛒</span><br><strong style="color: #92400E; font-size: 18px;">You left something behind!</strong><br><span style="color: #A16207;">Complete your purchase before items sell out.</span>' } },
        },
      },
      {
        type: AdvancedType.BUTTON,
        title: "Buy Now Button",
        payload: {
          attributes: {
            "background-color": "#10B981",
            color: "#ffffff",
            "border-radius": "8px",
            "font-size": "18px",
            "font-weight": "700",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "0px",
            "inner-padding": "16px 48px",
            href: "#",
          },
          data: { value: { content: "Buy Now →" } },
        },
      },
      {
        type: AdvancedType.BUTTON,
        title: "Add to Cart",
        payload: {
          attributes: {
            "background-color": "#F59E0B",
            color: "#ffffff",
            "border-radius": "8px",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "0px",
            "inner-padding": "14px 32px",
            href: "#",
          },
          data: { value: { content: "🛒 Add to Cart" } },
        },
      },
    ],
  },

  // ============================================================
  // ALERTS & NOTIFICATIONS
  // ============================================================
  {
    label: "Alerts & Status",
    active: true,
    blocks: [
      // Alert Boxes
      {
        type: AdvancedType.TEXT,
        title: "Info Alert",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
            "background-color": "#EFF6FF",
            color: "#1E40AF",
          },
          data: { value: { content: "ℹ️ <strong>Heads up!</strong> This is an informational message to keep you updated." } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Success Alert",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
            "background-color": "#ECFDF5",
            color: "#065F46",
          },
          data: { value: { content: "✅ <strong>Success!</strong> Your action has been completed successfully." } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Warning Alert",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
            "background-color": "#FFFBEB",
            color: "#92400E",
          },
          data: { value: { content: "⚠️ <strong>Warning!</strong> Please review this important information before proceeding." } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Error Alert",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px 20px",
            "background-color": "#FEF2F2",
            color: "#991B1B",
          },
          data: { value: { content: "❌ <strong>Error!</strong> Something went wrong. Please try again or contact support." } },
        },
      },
      // Countdown/Urgency
      {
        type: AdvancedType.TEXT,
        title: "Countdown Timer",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
            "background-color": "#0F172A",
            color: "#ffffff",
          },
          data: { value: { content: '<span style="font-size: 14px; text-transform: uppercase; letter-spacing: 2px;">Offer ends in</span><br><span style="font-size: 36px; font-weight: 700; font-family: monospace;"><span style="background: #8B5CF6; padding: 8px 12px; border-radius: 4px; margin: 0 2px;">02</span>:<span style="background: #8B5CF6; padding: 8px 12px; border-radius: 4px; margin: 0 2px;">14</span>:<span style="background: #8B5CF6; padding: 8px 12px; border-radius: 4px; margin: 0 2px;">37</span></span><br><span style="font-size: 12px; color: #94A3B8;">HOURS : MINS : SECS</span>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Expiry Notice",
        payload: {
          attributes: {
            align: "center",
            color: "#EF4444",
            "font-size": "16px",
            "font-weight": "600",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px",
          },
          data: { value: { content: "⏰ This offer expires in 24 hours!" } },
        },
      },
      // Progress/Status
      {
        type: AdvancedType.TEXT,
        title: "Progress Bar",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: '<span style="color: #64748B;">Profile completion: <strong>75%</strong></span><br><div style="background: #E2E8F0; border-radius: 10px; height: 12px; margin-top: 10px; overflow: hidden;"><div style="background: linear-gradient(90deg, #8B5CF6, #2DD4BF); height: 100%; width: 75%; border-radius: 10px;"></div></div>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Status Pending",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px",
          },
          data: { value: { content: '<span style="background: #FEF3C7; color: #92400E; padding: 6px 16px; border-radius: 20px; font-weight: 600;">⏳ Pending</span>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Status Completed",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "15px",
          },
          data: { value: { content: '<span style="background: #ECFDF5; color: #065F46; padding: 6px 16px; border-radius: 20px; font-weight: 600;">✓ Completed</span>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "Order Timeline",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: '<div style="border-left: 3px solid #8B5CF6; padding-left: 20px;"><div style="margin-bottom: 20px;"><span style="color: #10B981; font-weight: 600;">✓ Order Placed</span><br><span style="color: #94A3B8; font-size: 12px;">Dec 15, 2024 at 2:30 PM</span></div><div style="margin-bottom: 20px;"><span style="color: #10B981; font-weight: 600;">✓ Payment Confirmed</span><br><span style="color: #94A3B8; font-size: 12px;">Dec 15, 2024 at 2:31 PM</span></div><div style="margin-bottom: 20px;"><span style="color: #8B5CF6; font-weight: 600;">● Shipped</span><br><span style="color: #94A3B8; font-size: 12px;">In transit - Expected Dec 18</span></div><div><span style="color: #94A3B8; font-weight: 600;">○ Delivered</span><br><span style="color: #94A3B8; font-size: 12px;">Pending</span></div></div>' } },
        },
      },
    ],
  },

  // ============================================================
  // ENGAGEMENT - Interactive elements
  // ============================================================
  {
    label: "Engagement",
    active: true,
    blocks: [
      // Video Thumbnail
      {
        type: AdvancedType.TEXT,
        title: "Video Thumbnail",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: '<div style="position: relative; background: linear-gradient(135deg, #8B5CF6, #2DD4BF); border-radius: 12px; padding: 60px; text-align: center;"><span style="font-size: 60px;">▶️</span><br><span style="color: white; font-weight: 600;">Watch the Demo (2:34)</span></div>' } },
        },
      },
      // App Store Badges
      {
        type: AdvancedType.TEXT,
        title: "App Store Badges",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: '<span style="background: #000000; color: white; padding: 10px 20px; border-radius: 8px; margin: 5px; display: inline-block;"> Download on the<br><strong style="font-size: 16px;">App Store</strong></span>&nbsp;&nbsp;<span style="background: #000000; color: white; padding: 10px 20px; border-radius: 8px; margin: 5px; display: inline-block;"> Get it on<br><strong style="font-size: 16px;">Google Play</strong></span>' } },
        },
      },
      // Survey/Poll
      {
        type: AdvancedType.TEXT,
        title: "Quick Poll",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
            "background-color": "#F1F5F9",
          },
          data: { value: { content: '<strong style="color: #0F172A; font-size: 18px;">How satisfied are you with our service?</strong><br><br><span style="font-size: 32px; cursor: pointer;">😍 😊 😐 😕 😢</span><br><span style="color: #64748B; font-size: 12px;">Click to rate</span>' } },
        },
      },
      {
        type: AdvancedType.TEXT,
        title: "NPS Survey",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
            "background-color": "#EFF6FF",
          },
          data: { value: { content: '<strong style="color: #1E40AF;">How likely are you to recommend us?</strong><br><br><span style="font-size: 14px; color: #64748B;">Not likely &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Very likely</span><br><span style="display: inline-block; margin: 5px;">' + [0,1,2,3,4,5,6,7,8,9,10].map(n => `<span style="display: inline-block; width: 28px; height: 28px; line-height: 28px; text-align: center; border: 1px solid #CBD5E1; border-radius: 4px; margin: 2px; font-size: 12px; color: #64748B;">${n}</span>`).join('') + '</span>' } },
        },
      },
      // Social Share
      {
        type: AdvancedType.TEXT,
        title: "Share Buttons",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: '<span style="color: #64748B;">Share this with friends:</span><br><br><span style="background: #1877F2; color: white; padding: 8px 16px; border-radius: 6px; margin: 4px;">Facebook</span>&nbsp;<span style="background: #1DA1F2; color: white; padding: 8px 16px; border-radius: 6px; margin: 4px;">Twitter</span>&nbsp;<span style="background: #0A66C2; color: white; padding: 8px 16px; border-radius: 6px; margin: 4px;">LinkedIn</span>' } },
        },
      },
      // Referral
      {
        type: AdvancedType.TEXT,
        title: "Referral Box",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
            "background-color": "#F0FDF4",
          },
          data: { value: { content: '<span style="font-size: 32px;">🎁</span><br><strong style="color: #166534; font-size: 20px;">Give $20, Get $20</strong><br><span style="color: #15803D;">Share your unique link with friends</span><br><br><div style="background: white; border: 2px solid #22C55E; border-radius: 8px; padding: 12px; font-family: monospace; color: #166534;">yoursite.com/ref/ABC123</div>' } },
        },
      },
    ],
  },

  // ============================================================
  // SAAS & TRANSACTIONAL
  // ============================================================
  {
    label: "SaaS & Account",
    active: true,
    blocks: [
      // Usage Stats
      {
        type: AdvancedType.TEXT,
        title: "Usage Stats",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
            "background-color": "#F8FAFC",
          },
          data: { value: { content: '<strong style="color: #0F172A; font-size: 16px;">Your Usage This Month</strong><br><br><table style="width: 100%; text-align: center;"><tr><td style="padding: 10px;"><span style="font-size: 28px; font-weight: 700; color: #8B5CF6;">2,847</span><br><span style="color: #64748B; font-size: 12px;">Messages Sent</span></td><td style="padding: 10px;"><span style="font-size: 28px; font-weight: 700; color: #2DD4BF;">156</span><br><span style="color: #64748B; font-size: 12px;">Conversations</span></td><td style="padding: 10px;"><span style="font-size: 28px; font-weight: 700; color: #10B981;">94%</span><br><span style="color: #64748B; font-size: 12px;">Resolution Rate</span></td></tr></table>' } },
        },
      },
      // Plan Info
      {
        type: AdvancedType.TEXT,
        title: "Current Plan",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
            "background-color": "#EFF6FF",
          },
          data: { value: { content: '<table style="width: 100%;"><tr><td><strong style="color: #1E40AF;">Current Plan:</strong> Pro</td><td style="text-align: right;"><span style="color: #1E40AF; font-weight: 600;">$49/month</span></td></tr><tr><td style="color: #64748B; padding-top: 5px;">Next billing date: Jan 15, 2025</td><td style="text-align: right;"><a href="#" style="color: #8B5CF6; text-decoration: underline;">Manage Plan</a></td></tr></table>' } },
        },
      },
      // Trial Expiry
      {
        type: AdvancedType.TEXT,
        title: "Trial Expiring",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
            "background-color": "#FEF3C7",
          },
          data: { value: { content: '<span style="font-size: 28px;">⏰</span><br><strong style="color: #92400E; font-size: 20px;">Your trial ends in 3 days</strong><br><span style="color: #A16207;">Upgrade now to keep all your data and continue using premium features.</span>' } },
        },
      },
      // Upgrade Prompt
      {
        type: AdvancedType.TEXT,
        title: "Upgrade Prompt",
        payload: {
          attributes: {
            align: "center",
            "font-size": "16px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
            "background-color": "#F5F3FF",
          },
          data: { value: { content: '<span style="font-size: 28px;">🚀</span><br><strong style="color: #5B21B6; font-size: 20px;">You\'re hitting your limits!</strong><br><span style="color: #7C3AED;">You\'ve used 90% of your monthly quota. Upgrade to Pro for unlimited access.</span>' } },
        },
      },
      // Invoice Summary
      {
        type: AdvancedType.TEXT,
        title: "Invoice Summary",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
            "background-color": "#FFFFFF",
            border: "1px solid #E2E8F0",
          },
          data: { value: { content: '<div style="border-bottom: 1px solid #E2E8F0; padding-bottom: 10px; margin-bottom: 15px;"><strong style="font-size: 18px; color: #0F172A;">Invoice #INV-2024-001</strong><br><span style="color: #64748B;">December 15, 2024</span></div><table style="width: 100%; color: #475569;"><tr><td>Pro Plan (Monthly)</td><td style="text-align: right;">$49.00</td></tr><tr><td>Add-on: Extra Storage</td><td style="text-align: right;">$9.00</td></tr><tr><td style="padding-top: 10px; border-top: 1px solid #E2E8F0;"><strong>Total</strong></td><td style="text-align: right; padding-top: 10px; border-top: 1px solid #E2E8F0;"><strong style="color: #0F172A; font-size: 18px;">$58.00</strong></td></tr></table>' } },
        },
      },
      // Login Alert
      {
        type: AdvancedType.TEXT,
        title: "New Login Alert",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
            "background-color": "#FEF2F2",
          },
          data: { value: { content: '<strong style="color: #991B1B;">🔐 New Sign-in Detected</strong><br><br><table style="color: #7F1D1D;"><tr><td style="padding: 3px 0;"><strong>Device:</strong></td><td style="padding-left: 10px;">Chrome on Windows</td></tr><tr><td style="padding: 3px 0;"><strong>Location:</strong></td><td style="padding-left: 10px;">New York, USA</td></tr><tr><td style="padding: 3px 0;"><strong>Time:</strong></td><td style="padding-left: 10px;">Dec 17, 2024 at 3:45 PM</td></tr></table><br><span style="font-size: 13px;">If this wasn\'t you, please secure your account immediately.</span>' } },
        },
      },
      // Welcome/Onboarding Step
      {
        type: AdvancedType.TEXT,
        title: "Onboarding Step",
        payload: {
          attributes: {
            align: "left",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: '<div style="display: flex; align-items: flex-start;"><span style="background: #8B5CF6; color: white; width: 32px; height: 32px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; margin-right: 15px; flex-shrink: 0;">1</span><div><strong style="color: #0F172A; font-size: 16px;">Connect Your First Chatbot</strong><br><span style="color: #64748B;">Get started by creating your first chatbot and connecting it to your website.</span></div></div>' } },
        },
      },
    ],
  },

  // ============================================================
  // FOOTERS - Email footer components
  // ============================================================
  {
    label: "Footers",
    active: true,
    blocks: [
      // Complete Footer
      {
        type: AdvancedType.TEXT,
        title: "Full Footer",
        payload: {
          attributes: {
            align: "center",
            "font-size": "13px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "30px 20px",
            "background-color": "#1E293B",
            color: "#94A3B8",
          },
          data: { value: { content: '<strong style="color: #ffffff; font-size: 16px;">Copilotly</strong><br><br>123 Business Street, Suite 100<br>San Francisco, CA 94102<br><br><a href="#" style="color: #2DD4BF; text-decoration: none;">Website</a> &nbsp;•&nbsp; <a href="#" style="color: #2DD4BF; text-decoration: none;">Support</a> &nbsp;•&nbsp; <a href="#" style="color: #2DD4BF; text-decoration: none;">Privacy Policy</a><br><br><span style="font-size: 11px;">You received this email because you signed up for Copilotly.<br><a href="#" style="color: #94A3B8;">Unsubscribe</a> | <a href="#" style="color: #94A3B8;">Update Preferences</a></span>' } },
        },
      },
      // Minimal Footer
      {
        type: AdvancedType.TEXT,
        title: "Minimal Footer",
        payload: {
          attributes: {
            align: "center",
            "font-size": "12px",
            color: "#94A3B8",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: '© 2024 Copilotly. All rights reserved.<br><a href="#" style="color: #64748B;">Unsubscribe</a> | <a href="#" style="color: #64748B;">Privacy Policy</a>' } },
        },
      },
      // Contact Footer
      {
        type: AdvancedType.TEXT,
        title: "Contact Footer",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
            "background-color": "#F8FAFC",
          },
          data: { value: { content: '<strong style="color: #0F172A;">Need Help?</strong><br><br>📧 support@copilotly.com<br>📞 1-800-123-4567<br>💬 Live chat available 24/7<br><br><span style="color: #64748B; font-size: 12px;">Mon-Fri 9AM-6PM EST</span>' } },
        },
      },
      // Unsubscribe Block
      {
        type: AdvancedType.TEXT,
        title: "Unsubscribe",
        payload: {
          attributes: {
            align: "center",
            color: "#94A3B8",
            "font-size": "11px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "20px",
          },
          data: { value: { content: 'You\'re receiving this email because you signed up at copilotly.com<br>Want to change how you receive these emails?<br><a href="#" style="color: #64748B;">Update your preferences</a> or <a href="#" style="color: #64748B;">unsubscribe from this list</a>' } },
        },
      },
      // Social Footer
      {
        type: AdvancedType.TEXT,
        title: "Social Footer",
        payload: {
          attributes: {
            align: "center",
            "font-size": "14px",
            "font-family": "'Segoe UI', Arial, sans-serif",
            padding: "25px",
          },
          data: { value: { content: '<span style="color: #64748B;">Follow us</span><br><br><span style="font-size: 24px;">📘 🐦 📸 💼 📺</span><br><br><span style="color: #94A3B8; font-size: 12px;">@copilotly on all platforms</span>' } },
        },
      },
    ],
  },

  // ============================================================
  // CONTENT BLOCKS - Basic building blocks
  // ============================================================
  {
    label: "Content Blocks",
    active: false,
    blocks: [
      {
        type: AdvancedType.TEXT,
        title: "Text Block",
      },
      {
        type: AdvancedType.IMAGE,
        title: "Image",
        payload: { attributes: { padding: "0px 0px 0px 0px" } },
      },
      {
        type: AdvancedType.BUTTON,
        title: "Button",
      },
      {
        type: AdvancedType.SOCIAL,
        title: "Social Links",
        payload: {
          attributes: {
            align: "center",
            "icon-size": "30px",
            mode: "horizontal",
            padding: "10px 0",
          },
        },
      },
      {
        type: AdvancedType.DIVIDER,
        title: "Divider",
      },
      {
        type: AdvancedType.SPACER,
        title: "Spacer",
      },
    ],
  },

  // ============================================================
  // LAYOUT PATTERNS
  // ============================================================
  {
    label: "Layout Patterns",
    active: false,
    displayType: "column",
    blocks: [
      {
        title: "2 columns",
        payload: [
          ["50%", "50%"],
          ["33%", "67%"],
          ["67%", "33%"],
          ["25%", "75%"],
          ["75%", "25%"],
        ],
      },
      {
        title: "3 columns",
        payload: [
          ["33.33%", "33.33%", "33.33%"],
          ["25%", "25%", "50%"],
          ["50%", "25%", "25%"],
        ],
      },
      { title: "4 columns", payload: [["25%", "25%", "25%", "25%"]] },
    ],
  },

  // ============================================================
  // ADVANCED COMPONENTS
  // ============================================================
  {
    label: "Advanced",
    active: true,
    blocks: [
      // Hero Banners - Multiple styles
      {
        type: AdvancedType.HERO,
        title: "Hero - Gradient",
        payload: {
          attributes: {
            "background-color": "#8B5CF6",
            "background-url": "",
            "background-position": "center center",
            mode: "fixed-height",
            height: "360px",
            "vertical-align": "middle",
            padding: "40px 20px",
          },
          data: {
            value: {},
          },
          children: [
            {
              type: AdvancedType.TEXT,
              attributes: {
                align: "center",
                color: "#ffffff",
                "font-size": "42px",
                "font-weight": "700",
                "font-family": "'Segoe UI', Arial, sans-serif",
                padding: "10px 20px",
              },
              data: {
                value: {
                  content: "Transform Your Business Today",
                },
              },
              children: [],
            },
            {
              type: AdvancedType.TEXT,
              attributes: {
                align: "center",
                color: "rgba(255,255,255,0.9)",
                "font-size": "18px",
                "font-family": "'Segoe UI', Arial, sans-serif",
                padding: "10px 40px 20px 40px",
                "line-height": "1.6",
              },
              data: {
                value: {
                  content: "Join thousands of companies already using our platform to grow faster and smarter.",
                },
              },
              children: [],
            },
            {
              type: AdvancedType.BUTTON,
              attributes: {
                align: "center",
                "background-color": "#2DD4BF",
                color: "#0F172A",
                "font-size": "16px",
                "font-weight": "600",
                "border-radius": "8px",
                padding: "10px 0",
                "inner-padding": "16px 40px",
                href: "#",
              },
              data: {
                value: {
                  content: "Get Started Free →",
                },
              },
              children: [],
            },
          ],
        },
      },
      {
        type: AdvancedType.HERO,
        title: "Hero - Image BG",
        payload: {
          attributes: {
            "background-color": "#1E293B",
            "background-url": "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&q=80",
            "background-position": "center center",
            mode: "fixed-height",
            height: "400px",
            "vertical-align": "middle",
            padding: "40px 20px",
          },
          data: {
            value: {},
          },
          children: [
            {
              type: AdvancedType.TEXT,
              attributes: {
                align: "center",
                color: "#ffffff",
                "font-size": "38px",
                "font-weight": "700",
                "font-family": "'Segoe UI', Arial, sans-serif",
                padding: "10px 20px",
              },
              data: {
                value: {
                  content: "Build Something Amazing",
                },
              },
              children: [],
            },
            {
              type: AdvancedType.TEXT,
              attributes: {
                align: "center",
                color: "rgba(255,255,255,0.85)",
                "font-size": "16px",
                "font-family": "'Segoe UI', Arial, sans-serif",
                padding: "10px 40px 25px 40px",
              },
              data: {
                value: {
                  content: "The all-in-one platform for modern teams",
                },
              },
              children: [],
            },
            {
              type: AdvancedType.BUTTON,
              attributes: {
                align: "center",
                "background-color": "#ffffff",
                color: "#8B5CF6",
                "font-size": "16px",
                "font-weight": "600",
                "border-radius": "8px",
                padding: "10px 0",
                "inner-padding": "14px 36px",
                href: "#",
              },
              data: {
                value: {
                  content: "Learn More",
                },
              },
              children: [],
            },
          ],
        },
      },
      {
        type: AdvancedType.HERO,
        title: "Hero - Minimal",
        payload: {
          attributes: {
            "background-color": "#F1F5F9",
            "background-url": "",
            mode: "fixed-height",
            height: "300px",
            "vertical-align": "middle",
            padding: "30px 20px",
          },
          data: {
            value: {},
          },
          children: [
            {
              type: AdvancedType.TEXT,
              attributes: {
                align: "center",
                color: "#0F172A",
                "font-size": "36px",
                "font-weight": "700",
                "font-family": "'Segoe UI', Arial, sans-serif",
                padding: "10px 20px",
              },
              data: {
                value: {
                  content: "Simple. Powerful. Effective.",
                },
              },
              children: [],
            },
            {
              type: AdvancedType.TEXT,
              attributes: {
                align: "center",
                color: "#64748B",
                "font-size": "16px",
                "font-family": "'Segoe UI', Arial, sans-serif",
                padding: "10px 40px 20px 40px",
              },
              data: {
                value: {
                  content: "Everything you need to succeed, nothing you don't.",
                },
              },
              children: [],
            },
            {
              type: AdvancedType.BUTTON,
              attributes: {
                align: "center",
                "background-color": "#8B5CF6",
                color: "#ffffff",
                "font-size": "16px",
                "font-weight": "600",
                "border-radius": "8px",
                padding: "10px 0",
                "inner-padding": "14px 36px",
                href: "#",
              },
              data: {
                value: {
                  content: "Start Now",
                },
              },
              children: [],
            },
          ],
        },
      },
      // Accordion - FAQ Style
      {
        type: AdvancedType.ACCORDION,
        title: "FAQ Accordion",
        payload: {
          attributes: {
            padding: "20px",
            border: "1px solid #E2E8F0",
            "icon-width": "16px",
            "icon-height": "16px",
            "icon-position": "right",
            "icon-align": "middle",
            "icon-wrapped-url": "https://i.imgur.com/xAiEX9M.png",
            "icon-unwrapped-url": "https://i.imgur.com/xAiEX9M.png",
          },
          data: {
            value: {},
          },
          children: [
            {
              type: "advanced_accordion_element",
              attributes: {
                "icon-width": "16px",
                "icon-height": "16px",
                "icon-position": "right",
                "icon-align": "middle",
              },
              data: {
                value: {},
              },
              children: [
                {
                  type: "advanced_accordion_title",
                  attributes: {
                    "font-size": "16px",
                    "font-family": "'Segoe UI', Arial, sans-serif",
                    "background-color": "#F8FAFC",
                    color: "#0F172A",
                    padding: "16px 20px",
                    "font-weight": "600",
                  },
                  data: {
                    value: {
                      content: "How do I get started?",
                    },
                  },
                  children: [],
                },
                {
                  type: "advanced_accordion_text",
                  attributes: {
                    "font-size": "14px",
                    "font-family": "'Segoe UI', Arial, sans-serif",
                    "background-color": "#ffffff",
                    color: "#475569",
                    padding: "16px 20px",
                    "line-height": "1.6",
                  },
                  data: {
                    value: {
                      content: "Getting started is easy! Simply sign up for a free account, follow our quick setup wizard, and you'll be ready to go in minutes. Our team is always here to help if you need assistance.",
                    },
                  },
                  children: [],
                },
              ],
            },
            {
              type: "advanced_accordion_element",
              attributes: {
                "icon-width": "16px",
                "icon-height": "16px",
                "icon-position": "right",
                "icon-align": "middle",
              },
              data: {
                value: {},
              },
              children: [
                {
                  type: "advanced_accordion_title",
                  attributes: {
                    "font-size": "16px",
                    "font-family": "'Segoe UI', Arial, sans-serif",
                    "background-color": "#F8FAFC",
                    color: "#0F172A",
                    padding: "16px 20px",
                    "font-weight": "600",
                  },
                  data: {
                    value: {
                      content: "What payment methods do you accept?",
                    },
                  },
                  children: [],
                },
                {
                  type: "advanced_accordion_text",
                  attributes: {
                    "font-size": "14px",
                    "font-family": "'Segoe UI', Arial, sans-serif",
                    "background-color": "#ffffff",
                    color: "#475569",
                    padding: "16px 20px",
                    "line-height": "1.6",
                  },
                  data: {
                    value: {
                      content: "We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers for annual plans. All payments are processed securely through our payment partners.",
                    },
                  },
                  children: [],
                },
              ],
            },
            {
              type: "advanced_accordion_element",
              attributes: {
                "icon-width": "16px",
                "icon-height": "16px",
                "icon-position": "right",
                "icon-align": "middle",
              },
              data: {
                value: {},
              },
              children: [
                {
                  type: "advanced_accordion_title",
                  attributes: {
                    "font-size": "16px",
                    "font-family": "'Segoe UI', Arial, sans-serif",
                    "background-color": "#F8FAFC",
                    color: "#0F172A",
                    padding: "16px 20px",
                    "font-weight": "600",
                  },
                  data: {
                    value: {
                      content: "Can I cancel my subscription anytime?",
                    },
                  },
                  children: [],
                },
                {
                  type: "advanced_accordion_text",
                  attributes: {
                    "font-size": "14px",
                    "font-family": "'Segoe UI', Arial, sans-serif",
                    "background-color": "#ffffff",
                    color: "#475569",
                    padding: "16px 20px",
                    "line-height": "1.6",
                  },
                  data: {
                    value: {
                      content: "Yes, absolutely! You can cancel your subscription at any time with no questions asked. Your access will continue until the end of your current billing period.",
                    },
                  },
                  children: [],
                },
              ],
            },
          ],
        },
      },
      // Carousel - Product/Feature showcase
      {
        type: AdvancedType.CAROUSEL,
        title: "Image Carousel",
        payload: {
          attributes: {
            align: "center",
            "border-radius": "8px",
            thumbnails: "visible",
            "tb-border": "2px solid #8B5CF6",
            "tb-border-radius": "4px",
            "tb-hover-border-color": "#2DD4BF",
            "tb-selected-border-color": "#8B5CF6",
            "tb-width": "60px",
            "icon-width": "32px",
            "left-icon": "https://i.imgur.com/xTh3hln.png",
            "right-icon": "https://i.imgur.com/xTh3hln.png",
            padding: "20px",
          },
          data: {
            value: {
              images: [
                {
                  src: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&q=80",
                  target: "_blank",
                  href: "#",
                  title: "Team Collaboration",
                },
                {
                  src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
                  target: "_blank",
                  href: "#",
                  title: "Analytics Dashboard",
                },
                {
                  src: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&q=80",
                  target: "_blank",
                  href: "#",
                  title: "Modern Workspace",
                },
              ],
            },
          },
          children: [],
        },
      },
      // Navbar - Navigation menu
      {
        type: AdvancedType.NAVBAR,
        title: "Navigation Bar",
        payload: {
          attributes: {
            align: "center",
            hamburger: "hamburger",
            "ico-open": "https://i.imgur.com/xTh3hln.png",
            "ico-close": "https://i.imgur.com/xTh3hln.png",
            "ico-padding": "10px",
            "ico-align": "center",
            "ico-color": "#8B5CF6",
            "ico-font-size": "30px",
            "ico-font-family": "'Segoe UI', Arial, sans-serif",
            "ico-text-transform": "none",
            "ico-text-decoration": "none",
            "ico-line-height": "30px",
          },
          data: {
            value: {
              links: [
                {
                  href: "#",
                  content: "Home",
                  color: "#8B5CF6",
                  "font-size": "14px",
                  "font-family": "'Segoe UI', Arial, sans-serif",
                  "font-weight": "600",
                  "text-decoration": "none",
                  "text-transform": "none",
                  target: "_blank",
                  padding: "15px 20px",
                },
                {
                  href: "#",
                  content: "Features",
                  color: "#475569",
                  "font-size": "14px",
                  "font-family": "'Segoe UI', Arial, sans-serif",
                  "font-weight": "500",
                  "text-decoration": "none",
                  "text-transform": "none",
                  target: "_blank",
                  padding: "15px 20px",
                },
                {
                  href: "#",
                  content: "Pricing",
                  color: "#475569",
                  "font-size": "14px",
                  "font-family": "'Segoe UI', Arial, sans-serif",
                  "font-weight": "500",
                  "text-decoration": "none",
                  "text-transform": "none",
                  target: "_blank",
                  padding: "15px 20px",
                },
                {
                  href: "#",
                  content: "Contact",
                  color: "#475569",
                  "font-size": "14px",
                  "font-family": "'Segoe UI', Arial, sans-serif",
                  "font-weight": "500",
                  "text-decoration": "none",
                  "text-transform": "none",
                  target: "_blank",
                  padding: "15px 20px",
                },
              ],
            },
          },
          children: [],
        },
      },
      // Table - Pricing/Feature comparison
      {
        type: AdvancedType.TABLE,
        title: "Pricing Table",
        payload: {
          attributes: {
            align: "center",
            padding: "20px",
            width: "100%",
            "font-family": "'Segoe UI', Arial, sans-serif",
          },
          data: {
            value: {
              content: `<table style="width: 100%; border-collapse: collapse; font-family: 'Segoe UI', Arial, sans-serif;">
                <thead>
                  <tr style="background-color: #8B5CF6; color: #ffffff;">
                    <th style="padding: 14px 16px; text-align: left; font-weight: 600;">Feature</th>
                    <th style="padding: 14px 16px; text-align: center; font-weight: 600;">Optimizer</th>
                    <th style="padding: 14px 16px; text-align: center; font-weight: 600;">Wizard</th>
                    <th style="padding: 14px 16px; text-align: center; font-weight: 600;">Maverick</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style="background-color: #F8FAFC;">
                    <td style="padding: 12px 16px; color: #0F172A;">Monthly Messages</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569;">1,000</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569;">10,000</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569;">Unlimited</td>
                  </tr>
                  <tr style="background-color: #ffffff;">
                    <td style="padding: 12px 16px; color: #0F172A;">Team Members</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569;">1</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569;">5</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569;">Unlimited</td>
                  </tr>
                  <tr style="background-color: #F8FAFC;">
                    <td style="padding: 12px 16px; color: #0F172A;">Analytics</td>
                    <td style="padding: 12px 16px; text-align: center; color: #94A3B8;">Basic</td>
                    <td style="padding: 12px 16px; text-align: center; color: #10B981;">✓ Advanced</td>
                    <td style="padding: 12px 16px; text-align: center; color: #10B981;">✓ Custom</td>
                  </tr>
                  <tr style="background-color: #ffffff;">
                    <td style="padding: 12px 16px; color: #0F172A;">Priority Support</td>
                    <td style="padding: 12px 16px; text-align: center; color: #EF4444;">✗</td>
                    <td style="padding: 12px 16px; text-align: center; color: #10B981;">✓</td>
                    <td style="padding: 12px 16px; text-align: center; color: #10B981;">✓ 24/7</td>
                  </tr>
                  <tr style="background-color: #F1F5F9;">
                    <td style="padding: 14px 16px; color: #0F172A; font-weight: 600;">Price</td>
                    <td style="padding: 14px 16px; text-align: center; color: #8B5CF6; font-weight: 700;">$9/mo</td>
                    <td style="padding: 14px 16px; text-align: center; color: #8B5CF6; font-weight: 700;">$49/mo</td>
                    <td style="padding: 14px 16px; text-align: center; color: #8B5CF6; font-weight: 700;">Custom</td>
                  </tr>
                </tbody>
              </table>`,
            },
          },
          children: [],
        },
      },
      {
        type: AdvancedType.TABLE,
        title: "Data Table",
        payload: {
          attributes: {
            align: "center",
            padding: "20px",
            width: "100%",
            "font-family": "'Segoe UI', Arial, sans-serif",
          },
          data: {
            value: {
              content: `<table style="width: 100%; border-collapse: collapse; font-family: 'Segoe UI', Arial, sans-serif; border: 1px solid #E2E8F0;">
                <thead>
                  <tr style="background-color: #F8FAFC;">
                    <th style="padding: 12px 16px; text-align: left; font-weight: 600; color: #0F172A; border-bottom: 2px solid #E2E8F0;">Item</th>
                    <th style="padding: 12px 16px; text-align: center; font-weight: 600; color: #0F172A; border-bottom: 2px solid #E2E8F0;">Quantity</th>
                    <th style="padding: 12px 16px; text-align: right; font-weight: 600; color: #0F172A; border-bottom: 2px solid #E2E8F0;">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style="padding: 12px 16px; color: #475569; border-bottom: 1px solid #E2E8F0;">Product Name</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569; border-bottom: 1px solid #E2E8F0;">2</td>
                    <td style="padding: 12px 16px; text-align: right; color: #475569; border-bottom: 1px solid #E2E8F0;">$99.00</td>
                  </tr>
                  <tr style="background-color: #F8FAFC;">
                    <td style="padding: 12px 16px; color: #475569; border-bottom: 1px solid #E2E8F0;">Service Fee</td>
                    <td style="padding: 12px 16px; text-align: center; color: #475569; border-bottom: 1px solid #E2E8F0;">1</td>
                    <td style="padding: 12px 16px; text-align: right; color: #475569; border-bottom: 1px solid #E2E8F0;">$9.99</td>
                  </tr>
                  <tr>
                    <td colspan="2" style="padding: 14px 16px; text-align: right; font-weight: 600; color: #0F172A;">Total:</td>
                    <td style="padding: 14px 16px; text-align: right; font-weight: 700; color: #8B5CF6; font-size: 18px;">$108.99</td>
                  </tr>
                </tbody>
              </table>`,
            },
          },
          children: [],
        },
      },
    ],
  },

  // ============================================================
  // STRUCTURE ELEMENTS
  // ============================================================
  {
    label: "Structure",
    active: false,
    blocks: [
      {
        type: AdvancedType.SECTION,
        title: "Section",
      },
      {
        type: AdvancedType.COLUMN,
        title: "Column",
      },
      {
        type: AdvancedType.GROUP,
        title: "Group",
      },
      {
        type: AdvancedType.WRAPPER,
        title: "Wrapper",
      },
    ],
  },

  // ============================================================
  // ILLUSTRATIONS - Visual assets
  // ============================================================
  {
    label: "Illustrations (unDraw)",
    active: false,
    blocks: [
      {
        type: AdvancedType.IMAGE,
        title: "Welcome",
        payload: {
          attributes: {
            src: "/images/illustrations/email/welcome.svg",
            alt: "Welcome",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Onboarding",
        payload: {
          attributes: {
            src: "/images/illustrations/email/onboarding.svg",
            alt: "Onboarding",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Steps",
        payload: {
          attributes: {
            src: "/images/illustrations/email/steps.svg",
            alt: "Steps",
            width: "260px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Success",
        payload: {
          attributes: {
            src: "/images/illustrations/email/proud.svg",
            alt: "Success",
            width: "260px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Teamwork",
        payload: {
          attributes: {
            src: "/images/illustrations/email/teamwork.svg",
            alt: "Teamwork",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Chat",
        payload: {
          attributes: {
            src: "/images/illustrations/email/chat.svg",
            alt: "Chat",
            width: "260px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Data",
        payload: {
          attributes: {
            src: "/images/illustrations/email/data-processing.svg",
            alt: "Data",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Gift",
        payload: {
          attributes: {
            src: "/images/illustrations/email/gift.svg",
            alt: "Gift",
            width: "240px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Idea",
        payload: {
          attributes: {
            src: "/images/illustrations/email/idea.svg",
            alt: "Idea",
            width: "260px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Payment",
        payload: {
          attributes: {
            src: "/images/illustrations/email/payment.svg",
            alt: "Payment",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Settings",
        payload: {
          attributes: {
            src: "/images/illustrations/email/settings.svg",
            alt: "Settings",
            width: "240px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Checklist",
        payload: {
          attributes: {
            src: "/images/illustrations/email/checklist.svg",
            alt: "Checklist",
            width: "240px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
    ],
  },

  // ============================================================
  // STORYSET ILLUSTRATIONS
  // ============================================================
  {
    label: "Illustrations (Storyset)",
    active: false,
    blocks: [
      {
        type: AdvancedType.IMAGE,
        title: "Chatbot 1",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/chatbot-1.svg",
            alt: "Chatbot",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Chatbot 2",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/chatbot-2.svg",
            alt: "Chatbot",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Innovation",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/innovation-1.svg",
            alt: "Innovation",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Data",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/data-extraction-1.svg",
            alt: "Data",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Programming",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/programming-1.svg",
            alt: "Programming",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Marketing",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/mobile-marketing-1.svg",
            alt: "Marketing",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Hello",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/hello-1.svg",
            alt: "Hello",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Newsletter",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/newsletter-1.svg",
            alt: "Newsletter",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Goals",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/goal-1.svg",
            alt: "Goals",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Team",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/good-team-1.svg",
            alt: "Team",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Support",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/active-support-1.svg",
            alt: "Support",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
      {
        type: AdvancedType.IMAGE,
        title: "Security",
        payload: {
          attributes: {
            src: "/images/illustrations/email/storyset/security-on-1.svg",
            alt: "Security",
            width: "280px",
            padding: "20px 0px",
            align: "center",
          },
        },
      },
    ],
  },
];

// Define available fonts with expanded options
export const fontList = [
  "Arial",
  "Tahoma",
  "Verdana",
  "Times New Roman",
  "Courier New",
  "Georgia",
  "Lato",
  "Montserrat",
  "Open Sans",
  "Roboto",
  "Source Sans Pro",
  "PT Sans",
  "Oswald",
  "Raleway",
  "Poppins",
  "Playfair Display",
  "Merriweather",
  "Nunito Sans",
  "Inter",
  "Work Sans",
].map((item) => ({ value: item, label: item }));

// Define custom brand colors for the color picker (Copilotly Brand)
export const brandColors = [
  "#8B5CF6", // Primary purple (violet-500)
  "#A855F7", // Secondary purple (purple-500)
  "#2DD4BF", // Accent teal
  "#1E293B", // Dark slate
  "#0EA5E9", // Sky blue
  "#10B981", // Success green
  "#F59E0B", // Warning amber
  "#EF4444", // Error red
  "#EC4899", // Pink
  "#F1F5F9", // Light background
  "#FFFFFF", // White
  "#0F172A", // Near black
];
